import { Injectable, NgZone } from '@angular/core';
import { TimeZoneInfo } from '@app/origami-api/utils/topic-utils';
import { DateTime } from 'luxon';
import { padStart } from 'lodash';
import { DateFormatStore } from '@app/session/date-format-store';
import { Converter } from '@app/shared/utils/converter';
import { SessionService } from '@app/session/session.service';

@Injectable({
  providedIn: 'root',
})
export class TimeZoneService {
  constructor(
    private readonly zone: NgZone,
    private readonly sessionService: SessionService,
  ) {}

  private getIsoLikeString(d: Date): string {
    return this.zone.runOutsideAngular<string>(() => {
      return `${d.getFullYear()}-${padStart(d.getMonth() + 1 + '', 2, '0')}-${padStart(
        d.getDate() + '',
        2,
        '0',
      )}T${padStart(d.getHours() + '', 2, '0')}:${padStart(d.getMinutes() + '', 2, '0')}:${padStart(
        d.getSeconds() + '',
        2,
        '0',
      )}.000Z`;
    });
  }

  public windowsZones: { ZoneKey: string; IanaZoneName: string }[];

  public getZoneKeyByIanaZoneName(ianaZoneName: string): string {
    const windowsZone = this.windowsZones.find((windowsZone) => windowsZone.IanaZoneName === ianaZoneName);
    return windowsZone?.ZoneKey;
  }

  public getIanaZoneNameByZoneKey(zoneKey: string): string {
    const windowsZone = this.windowsZones.find((windowsZone) => windowsZone.ZoneKey === zoneKey);
    return windowsZone?.IanaZoneName;
  }

  public fromTzToTz(options: {
    dateTime: Date | 'now';
    fromTz: TimeZoneInfo;
    toTz: TimeZoneInfo;
    keepLocalTime?: boolean;
    format?: string;
    asString?: boolean;
  }): Date | string {
    if (!options?.dateTime) {
      return null;
    }

    return this.zone.runOutsideAngular<Date>(() => {
      options.format =
        Converter.DateAndTime.toKendoDateFormat(options.format) ||
        Converter.DateAndTime.toKendoDateFormat(DateFormatStore.dateTimeFormat);

      let from;
      /*
      if (keepLocalTime) {
        from = DateTime.fromJSDate(dateTime, { zone: 'local' });
        from = from.setZone(fromTz.IanaZoneName, { keepLocalTime: true });
      } else {
        from = DateTime.fromJSDate(dateTime, { zone: fromTz.IanaZoneName });
      }
      */

      if (options.dateTime === 'now') {
        options.dateTime = DateTime.fromJSDate(new Date(), { zone: options.fromTz.IanaZoneName }).toJSDate() as Date;
      }

      from = DateTime.fromISO(this.getIsoLikeString(options.dateTime), { zone: 'utc' });
      from = from.setZone(options.fromTz.IanaZoneName, { keepLocalTime: true });

      const to = from.setZone(options.toTz.IanaZoneName);

      // console.log("Luxon:", options.dateTime, this.getIsoLikeString(options.dateTime));
      // console.log(options.fromTz.IanaZoneName, from.toFormat(options.format));
      // console.log(options.toTz.IanaZoneName, to.toFormat(options.format));

      if (options.asString) {
        return to.toFormat(options.format);
      } else {
        return DateTime.fromFormat(to.toFormat(options.format), options.format).toJSDate();
      }
    });
  }

  public convertBrowserDateToUTCDate(browserDate: Date): Date {
    if (
      !Intl?.DateTimeFormat().resolvedOptions().timeZone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone === 'Etc/UTC' ||
      !(browserDate instanceof Date)
    ) {
      return browserDate;
    }
    const roomDateTime = DateTime.fromObject(
      {
        year: browserDate.getFullYear(),
        month: browserDate.getMonth() + 1,
        day: browserDate.getDate(),
        hour: browserDate.getHours(),
        minute: browserDate.getMinutes(),
      },
      {
        zone: 'Etc/UTC',
      },
    );
    return roomDateTime.toJSDate();
  }

  public convertDomainDateToBrowserDate(domain: Date): Date {
    if (
      !this.sessionService.domain?.timeZone?.IanaZoneName ||
      !Intl?.DateTimeFormat().resolvedOptions().timeZone ||
      this.sessionService.domain?.timeZone?.IanaZoneName === Intl.DateTimeFormat().resolvedOptions().timeZone ||
      !(domain instanceof Date)
    ) {
      return domain;
    }
    let browserDateTime = DateTime.fromJSDate(domain);
    const domainTime = browserDateTime.setZone(this.sessionService.domain.timeZone.IanaZoneName);
    browserDateTime = DateTime.fromObject(
      {
        year: domainTime.year,
        month: domainTime.month,
        day: domainTime.day,
        hour: domainTime.hour,
        minute: domainTime.minute,
      },
      {
        zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    );
    return browserDateTime.toJSDate();
  }

  public convertRoomDateToBrowserDate(roomDate: Date, roomTimeZone?: TimeZoneInfo): Date {
    const roomTZName = this.sessionService.room?.timeZone?.IanaZoneName || roomTimeZone.IanaZoneName;
    if (
      !roomTZName ||
      !Intl?.DateTimeFormat().resolvedOptions().timeZone ||
      roomTZName === Intl.DateTimeFormat().resolvedOptions().timeZone ||
      !(roomDate instanceof Date)
    ) {
      return roomDate;
    }
    let browserDateTime = DateTime.fromJSDate(roomDate);
    const roomDateTime = browserDateTime.setZone(roomTZName);
    browserDateTime = DateTime.fromObject(
      {
        year: roomDateTime.year,
        month: roomDateTime.month,
        day: roomDateTime.day,
        hour: roomDateTime.hour,
        minute: roomDateTime.minute,
      },
      {
        zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    );
    return browserDateTime.toJSDate();
  }

  public convertBrowserDateToRoomDate(browserDate: Date): Date {
    if (
      !this.sessionService.room?.timeZone?.IanaZoneName ||
      !Intl?.DateTimeFormat().resolvedOptions().timeZone ||
      this.sessionService.room.timeZone.IanaZoneName === Intl.DateTimeFormat().resolvedOptions().timeZone ||
      !(browserDate instanceof Date)
    ) {
      return browserDate;
    }
    const ms = browserDate.getTime();
    const roomOffsetMs =
      DateTime.fromJSDate(browserDate).setZone(this.sessionService.room.timeZone.IanaZoneName).offset * 60000;
    const browserOffsetMs = DateTime.fromJSDate(browserDate).offset * 60000;
    const msToDisplay = ms - browserOffsetMs + roomOffsetMs;
    return new Date(msToDisplay);
  }

  public getRoomEOD(): Date {
    const currentBrowserDate = new Date();
    const currentRoomDate = this.convertBrowserDateToRoomDate(currentBrowserDate);
    currentRoomDate.setHours(23, 59, 59, 997);
    return currentRoomDate;
  }

  public setDateToRoomTimeZoneEndOfDay(date: Date): Date {
    const endOfDay = DateTime.fromJSDate(date).setZone(this.sessionService.room.timeZone.IanaZoneName).endOf('day');
    const { year, month, day } = endOfDay;

    return new Date(year, month - 1, day, 23, 59, 59, 997);
  }

  public setDateToRoomTimeZoneStartOfDay(date: Date): Date {
    const startOfDay = DateTime.fromJSDate(date).setZone(this.sessionService.room.timeZone.IanaZoneName).startOf('day');
    const { year, month, day } = startOfDay;

    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  public convertDomainDateToRoomDate(domainDate: Date, roomTimeZone?: TimeZoneInfo): Date {
    const roomTZName = roomTimeZone?.IanaZoneName || this.sessionService.room?.timeZone?.IanaZoneName;
    if (!roomTZName || !(domainDate instanceof Date)) {
      return domainDate;
    }
    const ms = domainDate.getTime();
    const roomOffsetMs = DateTime.fromJSDate(domainDate).setZone(roomTZName).offset * 60000;
    const browserOffsetMs = DateTime.fromJSDate(domainDate).offset * 60000;
    const msToDisplay = ms - browserOffsetMs + roomOffsetMs;
    return new Date(msToDisplay);
  }

  public convertServerDateToRoomDate(domainDate: Date, considerBrowserOffset?: boolean, roomTZ?: TimeZoneInfo): Date {
    const roomTimeZoneInfo = roomTZ || this.sessionService.room?.timeZone;
    if (!this.sessionService.domain?.timeZone?.IanaZoneName || !roomTimeZoneInfo || !(domainDate instanceof Date)) {
      return domainDate;
    }
    const ms = domainDate.getTime();
    const roomOffsetMs = DateTime.fromJSDate(domainDate).setZone(roomTimeZoneInfo.IanaZoneName).offset * 60000;
    const serverOffsetMs =
      DateTime.fromJSDate(domainDate).setZone(this.sessionService.domain.timeZone.IanaZoneName).offset * 60000;
    const browserOffsetMs = DateTime.fromJSDate(domainDate).offset * 60000;
    const msToDisplay = considerBrowserOffset
      ? ms - browserOffsetMs + roomOffsetMs - serverOffsetMs
      : ms + roomOffsetMs - serverOffsetMs;
    return new Date(msToDisplay);
  }

  public convertServerDateToBrowserDate(serverDate: Date, considerBrowserOffset?: boolean): Date {
    if (!this.sessionService.domain?.timeZone?.IanaZoneName || !(serverDate instanceof Date)) {
      return serverDate;
    }
    const ms = serverDate.getTime();
    const serverOffsetMs =
      DateTime.fromJSDate(serverDate).setZone(this.sessionService.domain.timeZone.IanaZoneName).offset * 60000;
    const browserOffsetMs = DateTime.fromJSDate(serverDate).offset * 60000;
    const msToDisplay = considerBrowserOffset ? ms - serverOffsetMs : ms + browserOffsetMs - serverOffsetMs;
    return new Date(msToDisplay);
  }

  public convertBrowserDateToServerDate(browserDate: Date): Date {
    if (
      !Intl?.DateTimeFormat().resolvedOptions().timeZone ||
      this.sessionService.domain.timeZone.IanaZoneName === Intl.DateTimeFormat().resolvedOptions().timeZone ||
      !(browserDate instanceof Date)
    ) {
      return browserDate;
    }
    const ms = browserDate.getTime();
    const serverOffsetMs =
      DateTime.fromJSDate(browserDate).setZone(this.sessionService.domain.timeZone.IanaZoneName).offset * 60000;
    const browserOffsetMs = DateTime.fromJSDate(browserDate).offset * 60000;
    const msToDisplay = ms - browserOffsetMs + serverOffsetMs;
    return new Date(msToDisplay);
  }

  // currently this.sessionService.domain has server timezone (it was done a long time ago)
  // TODO: need to add sessionService.server and then
  // We need to rename functions and use this.sessionService.server when it's ready
  public convertRoomDateToDomainDate(
    roomDate: Date,
    considerBrowserOffset?: boolean,
    roomTimeZone?: TimeZoneInfo,
  ): Date {
    const roomTZName = this.sessionService.room?.timeZone?.IanaZoneName || roomTimeZone?.IanaZoneName;
    if (!this.sessionService.domain?.timeZone?.IanaZoneName || !roomTZName || !(roomDate instanceof Date)) {
      return roomDate;
    }
    const ms = roomDate.getTime();
    const roomOffsetMs = DateTime.fromJSDate(roomDate).setZone(roomTZName).offset * 60000;
    const domainOffsetMs =
      DateTime.fromJSDate(roomDate).setZone(this.sessionService.domain.timeZone.IanaZoneName).offset * 60000;
    const browserOffsetMs = DateTime.fromJSDate(roomDate).offset * 60000;
    const msToSend = considerBrowserOffset
      ? ms - roomOffsetMs + domainOffsetMs + browserOffsetMs
      : ms - roomOffsetMs + domainOffsetMs;
    return new Date(msToSend);
  }

  public formatDate(date: Date, customFormat?: string, withTimeZoneAbbr?: boolean, ianaZoneName?: string): string {
    ianaZoneName =
      ianaZoneName ||
      (this.sessionService.room?.id
        ? this.sessionService.room?.timeZone.IanaZoneName
        : Intl.DateTimeFormat().resolvedOptions().timeZone);
    const dateTime = DateTime.fromJSDate(date, {
      zone: ianaZoneName,
    });
    const timeZoneAbbr = dateTime.toFormat('ZZZZ');
    const formattedDate = dateTime.setZone(ianaZoneName).toFormat(customFormat ?? 'dd MMM yyyy HH:mm:ss');
    return withTimeZoneAbbr ? formattedDate + ' ' + timeZoneAbbr : formattedDate;
  }

  public getFormattedDateWithoutRoomTimeZone(date: Date, useTimePart?: boolean): string {
    const dateTime = DateTime.fromJSDate(date, {
      zone: this.sessionService.domain.timeZone.IanaZoneName,
    });

    const format = useTimePart ? DateFormatStore.luxonDateTimeFormatWithTime : DateFormatStore.luxonDateTimeFormat;
    return dateTime.toFormat(format);
  }

  public getFormattedDateWithRoomTimeZone(date: Date, useTimePart = true, skipTzAbbr?: boolean): string {
    const timeZone = this.sessionService.room?.id
      ? this.sessionService.room?.timeZone
      : this.sessionService.domain.timeZone;
    const dateTime = DateTime.fromJSDate(date, {
      zone: this.sessionService.domain.timeZone.IanaZoneName,
    }).setZone(timeZone.IanaZoneName);

    const format = useTimePart ? DateFormatStore.luxonDateTimeFormatWithTime : DateFormatStore.luxonDateTimeFormat;
    const timeZoneAbbr = dateTime.toFormat('ZZZZ');
    return skipTzAbbr ? dateTime.toFormat(format) : dateTime.toFormat(format) + ' ' + timeZoneAbbr;
  }

  public getRoomToDomainZoneShift(date: Date): number {
    if (
      !this.sessionService.domain?.timeZone?.IanaZoneName ||
      !this.sessionService.room?.timeZone?.IanaZoneName ||
      !(date instanceof Date)
    ) {
      return 0;
    }
    const roomOffsetHr = DateTime.fromJSDate(date).setZone(this.sessionService.room.timeZone.IanaZoneName).offset / 60;
    const domainOffsetHr =
      DateTime.fromJSDate(date).setZone(this.sessionService.domain.timeZone.IanaZoneName).offset / 60;

    return Math.round(roomOffsetHr - domainOffsetHr);
  }
}
